<template>
  <div class="centralizador-root">

    <!-- ── Header y Selector de Programa ──────────────────────────── -->
    <div class="dialog-header px-4 py-3 d-flex align-center justify-space-between flex-wrap gap-3 border-bottom">
      
      <!-- Titulo -->
      <div class="d-flex align-center">
        <div class="filter-header-icon mr-3">
          <v-icon color="primary" size="22">mdi-table-large</v-icon>
        </div>
        <div>
          <div class="text-subtitle-1 font-weight-bold slate-900--text" style="line-height: 1.2;">
            Acta Centralizador
          </div>
          <div class="text-caption grey--text text--darken-1" style="line-height: 1.2;">
            Consolidado y gestión de notas por programa
          </div>
        </div>
      </div>

      <!-- Autocomplete de búsqueda de programa -->
      <div class="search-program-container flex-grow-1 mx-2">
        <v-autocomplete
          v-model="programaSeleccionado"
          :items="programas"
          :loading="buscandoProgramas"
          :search-input.sync="searchPrograma"
          item-text="nombreCompuesto"
          item-value="id"
          placeholder="Buscar programa por nombre o código..."
          dense
          outlined
          hide-details
          clearable
          no-filter
          return-object
          prepend-inner-icon="mdi-school-outline"
          class="rounded-lg custom-input"
          :no-data-text="buscandoProgramas ? 'Buscando programas...' : 'Sin resultados'"
          @change="onProgramaSeleccionado">
          <template #item="{ item }">
            <v-list-item-content class="py-2">
              <v-list-item-title class="autocomplete-title">
                {{ item.nombreCompuesto }}
              </v-list-item-title>
              <v-list-item-subtitle class="autocomplete-codigo mt-1">
                <span class="chip-mini chip-code mr-1">
                  <v-icon x-small color="primary" class="mr-0.5">mdi-barcode</v-icon>
                  {{ item.codigo }}
                </span>
                <span v-if="item.codigoUniversidad" class="chip-mini chip-uni mr-1">
                  <v-icon x-small color="purple darken-2" class="mr-0.5">mdi-bank</v-icon>
                  {{ item.codigoUniversidad }}
                </span>
                <span v-if="item.sede" class="chip-mini chip-sede">
                  <v-icon x-small color="light-blue darken-3" class="mr-0.5">mdi-map-marker-outline</v-icon>
                  {{ item.sede }}
                </span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
          <template #selection="{ item }">
            <span class="autocomplete-selected text-truncate font-weight-medium">
              {{ item.nombreCompuesto }}
            </span>
          </template>
        </v-autocomplete>
      </div>

      <!-- Acciones de cabecera -->
      <div class="d-flex align-center gap-2 flex-wrap">
        <v-btn
          depressed
          color="primary"
          class="rounded-lg action-btn apply-btn compact-btn px-4"
          :loading="cargando"
          :disabled="!programaId"
          @click="buscarKardex">
          <v-icon left size="18">mdi-table-search</v-icon>
          Ver Acta
        </v-btn>

        <span v-if="kardex" class="req-badge req-success py-1 px-2.5 text-caption">
          <v-icon x-small left color="green darken-3">mdi-check-circle</v-icon>
          Cargado
        </span>

        <v-btn
          v-if="kardex"
          depressed
          outlined
          color="error"
          class="rounded-lg action-btn pdf-btn compact-btn px-3"
          @click="abrirDialogCentralizado">
          <v-icon left color="error" size="18">mdi-file-pdf-box</v-icon>
          Centralizador PDF
        </v-btn>

        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              depressed
              outlined
              color="primary"
              class="rounded-lg action-btn compact-btn-icon"
              :disabled="!programaId || cargando"
              @click="buscarKardex">
              <v-icon size="18">mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Recargar datos</span>
        </v-tooltip>
      </div>

    </div>

    <!-- ── Loading State ────────────────────────────────────────────── -->
    <div v-if="cargando" class="px-6 py-6">
      <div class="state-box">
        <div class="loading-spinner-wrap">
          <v-progress-circular indeterminate color="primary" size="52" width="3" />
        </div>
        <p class="state-text mt-4">Cargando acta centralizadora...</p>
        <p v-if="programaSeleccionado" class="state-subtext">
          {{ programaSeleccionado.nombreCompuesto }}
        </p>
      </div>
    </div>

    <!-- ── Contenido del Kardex ─────────────────────────────────────── -->
    <div v-else-if="kardex" class="px-5 pt-4 pb-6 content-fade">

      <!-- Tarjeta de Información y Métricas del Programa -->
      <v-card class="program-card mb-4" elevation="0">
        <div class="program-card-body">
          <div class="d-flex flex-column">
            
            <!-- Fila de Badges y Métricas inline (mismo tamaño) -->
            <div class="d-flex align-center flex-wrap gap-2 mb-2">
              <span class="program-code-badge">
                <v-icon x-small color="primary" class="mr-1">mdi-barcode</v-icon>
                {{ kardex.programa.codigo }}
              </span>
              
              <span class="custom-badge badge-xs state-version">
                V{{ kardex.programa.version }}
              </span>

              <span
                v-if="kardex.programa.programaUniversidad"
                class="custom-badge badge-xs badge-uni"
                :title="kardex.programa.programaUniversidad.nombre">
                <v-icon x-small color="purple darken-2" class="mr-1">mdi-bank</v-icon>
                {{ kardex.programa.programaUniversidad.codigo }}
              </span>

              <span
                v-if="kardex.programa.sede"
                class="custom-badge badge-xs badge-sede">
                <v-icon x-small color="light-blue darken-3" class="mr-1">mdi-map-marker-outline</v-icon>
                {{ kardex.programa.sede }}
              </span>

              <span class="custom-badge badge-xs badge-threshold">
                <v-icon x-small color="blue-grey" class="mr-1">mdi-shield-check-outline</v-icon>
                Nota de aprobación: <strong class="ml-1">{{ notaAprobacion }} pts</strong>
              </span>

              <!-- Separador visual -->
              <span class="grey--text text--lighten-1 mx-1">|</span>

              <!-- Estadísticas inline con el mismo tamaño -->
              <span class="custom-badge badge-xs badge-stat-blue">
                <v-icon x-small color="primary" class="mr-1">mdi-book-open-page-variant-outline</v-icon>
                {{ kardex.modulos.length }} módulos
              </span>

              <span class="custom-badge badge-xs badge-stat-slate">
                <v-icon x-small color="blue-grey darken-1" class="mr-1">mdi-account-group-outline</v-icon>
                {{ kardex.estudiantes.length }} estudiantes
              </span>

              <span class="custom-badge badge-xs badge-stat-emerald">
                <v-icon x-small color="success" class="mr-1">mdi-check-circle-outline</v-icon>
                {{ estudiantesAprobados }} aprobados
              </span>

              <span class="custom-badge badge-xs badge-stat-rose">
                <v-icon x-small color="error" class="mr-1">mdi-close-circle-outline</v-icon>
                {{ estudiantesReprobados }} reprobados
              </span>

              <span class="custom-badge badge-xs" :class="porcentajeAprobados >= 70 ? 'badge-stat-emerald' : 'badge-stat-amber'">
                <v-icon x-small :color="porcentajeAprobados >= 70 ? 'success' : 'amber darken-3'" class="mr-1">mdi-percent-outline</v-icon>
                {{ porcentajeAprobados }}% de aprobación
              </span>
            </div>

            <!-- Nombre del Programa -->
            <div class="prog-nombre">
              {{ kardex.programa.nombre }}
            </div>

          </div>
        </div>
      </v-card>

      <!-- Tabla de Módulos (Acordeón) -->
      <v-expansion-panels v-model="modulosPanel" flat class="modulos-panel mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header class="modulos-panel-header">
            <div class="d-flex align-center">
              <div class="header-icon-mini mr-2">
                <v-icon small color="primary">mdi-view-list</v-icon>
              </div>
              <span class="section-label mb-0">Módulos y Docentes Asignados</span>
              <span class="custom-badge badge-xs state-count ml-2">
                {{ kardex.modulos.length }} módulos
              </span>
            </div>
            <template #actions>
              <div class="d-flex align-center text-caption primary--text font-weight-medium mr-1">
                <span>{{ modulosPanel === 0 ? 'Ocultar detalle' : 'Ver detalle de actas' }}</span>
                <v-icon small color="primary" class="ml-1">
                  {{ modulosPanel === 0 ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </div>
            </template>
          </v-expansion-panel-header>
          
          <v-expansion-panel-content class="modulos-panel-content">
            <div class="table-container">
              <table class="modulos-table">
                <thead>
                  <tr>
                    <th class="text-left" style="width: 80px;">N°</th>
                    <th class="text-left" style="width: 45%;">MÓDULO</th>
                    <th class="text-left" style="width: 35%;">DOCENTE ASIGNADO</th>
                    <th class="text-center" style="width: 15%;">ACTAS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="modulo in kardex.modulos"
                    :key="modulo.id"
                    class="modulo-row">
                    <td>
                      <span class="modulo-num">{{ toRoman(modulo.numero) }}</span>
                    </td>
                    <td class="td-modulo">
                      <div class="modulo-name-wrapper">
                        <span class="modulo-title-text">{{ modulo.nombre }}</span>
                      </div>
                    </td>
                    <td>
                      <div v-if="modulo.docente" class="docente-info">
                        <div class="docente-name">
                          <v-icon x-small class="mr-1 primary--text">mdi-account-tie</v-icon>
                          {{ modulo.docente }}
                        </div>
                        <div v-if="modulo.docenteCi" class="docente-ci">
                          <v-icon x-small class="mr-1 grey--text">mdi-card-account-details-outline</v-icon>
                          CI: {{ modulo.docenteCi }}
                        </div>
                      </div>
                      <span v-else class="docente-unassigned">
                        <v-icon x-small class="mr-1" color="amber darken-3">mdi-account-alert-outline</v-icon>
                        Sin docente asignado
                      </span>
                    </td>
                    <td class="text-center">
                      <v-btn
                        small
                        depressed
                        outlined
                        color="primary"
                        class="rounded-lg module-acta-btn px-2"
                        @click="abrirDialogModulo(modulo)">
                        <v-icon x-small left>mdi-file-document-outline</v-icon>
                        Ver Actas
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Barra de Filtros y Búsqueda de Calificaciones -->
      <div class="matrix-toolbar d-flex align-center justify-space-between mb-3 flex-wrap gap-2">
        <div class="d-flex align-center flex-wrap gap-2">
          <div class="section-label">
            <v-icon small color="primary" class="mr-1.5">mdi-table-account</v-icon>
            Matriz de Calificaciones
          </div>
          <span class="custom-badge badge-xs state-default">
            {{ estudiantesFiltrados.length }} de {{ kardex.estudiantes.length }} estudiantes
          </span>
        </div>

        <!-- Filtros rápidos y buscador -->
        <div class="d-flex align-center gap-2 flex-wrap">
          
          <!-- Filtros de estado -->
          <div class="filter-tabs-wrap">
            <button
              class="filter-tab-btn"
              :class="{ 'filter-tab-active': filtroEstadoEstudiante === 'todos' }"
              @click="filtroEstadoEstudiante = 'todos'">
              Todos ({{ kardex.estudiantes.length }})
            </button>
            <button
              class="filter-tab-btn tab-success"
              :class="{ 'filter-tab-active': filtroEstadoEstudiante === 'aprobados' }"
              @click="filtroEstadoEstudiante = 'aprobados'">
              Aprobados ({{ estudiantesAprobados }})
            </button>
            <button
              class="filter-tab-btn tab-error"
              :class="{ 'filter-tab-active': filtroEstadoEstudiante === 'reprobados' }"
              @click="filtroEstadoEstudiante = 'reprobados'">
              Reprobados ({{ estudiantesReprobados }})
            </button>
            <button
              v-if="estudiantesConNivelacion > 0"
              class="filter-tab-btn tab-warning"
              :class="{ 'filter-tab-active': filtroEstadoEstudiante === 'nivelacion' }"
              @click="filtroEstadoEstudiante = 'nivelacion'">
              Con Nivelación ({{ estudiantesConNivelacion }})
            </button>
          </div>

          <!-- Buscador por nombre/CI -->
          <div style="width: 250px;">
            <v-text-field
              v-model="busquedaEstudiante"
              placeholder="Buscar estudiante o CI..."
              dense
              outlined
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              class="rounded-lg custom-input custom-input-sm" />
          </div>
        </div>
      </div>

      <!-- Tabla Centralizadora de Calificaciones -->
      <div class="detalle-pago-docente-container content-fade">
        <table class="grades-table w-100">
          <thead>
            <tr>
              <th class="th-idx sticky-col text-center">#</th>
              <th class="th-nombre sticky-col-2 text-left">APELLIDOS Y NOMBRES</th>
              <th
                v-for="modulo in kardex.modulos"
                :key="modulo.id"
                class="th-nota text-center"
                :title="`Módulo ${toRoman(modulo.numero)}: ${modulo.nombre}`">
                <div class="th-modulo-header">
                  <span class="th-romano">{{ toRoman(modulo.numero) }}</span>
                </div>
              </th>
            </tr>
          </thead>
          
          <tbody v-if="estudiantesFiltrados.length">
            <tr
              v-for="(estudiante, index) in estudiantesFiltrados"
              :key="estudiante.id"
              class="student-row">
              <td class="td-idx sticky-col text-center">
                {{ index + 1 }}
              </td>
              <td class="td-nombre sticky-col-2 text-left">
                <div class="student-cell-wrap">
                  <div class="student-avatar-wrap">
                    {{ getInitials(estudiante.nombreCompleto) }}
                  </div>
                  <div class="student-details">
                    <span class="student-name">{{ estudiante.nombreCompleto }}</span>
                    <span class="student-ci">
                      <v-icon x-small class="mr-1 grey--text text--darken-1">mdi-card-account-details-outline</v-icon>
                      CI: {{ estudiante.ci }}
                    </span>
                  </div>
                </div>
              </td>
              <td
                v-for="modulo in kardex.modulos"
                :key="modulo.id"
                class="td-nota text-center"
                :class="{ 'td-nota--clickable': esReprobado(estudiante.notas[modulo.id]) }"
                :title="notaTooltip(estudiante.notas[modulo.id])"
                @click="esReprobado(estudiante.notas[modulo.id]) && abrirDialogNivelacion(estudiante, modulo)">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                      :class="['nota-badge', notaClass(estudiante.notas[modulo.id])]">
                      {{ formatNota(estudiante.notas[modulo.id]) }}
                      <v-icon
                        v-if="esReprobado(estudiante.notas[modulo.id])"
                        x-small
                        class="ml-0.5 action-indicator-icon"
                        color="error">
                        mdi-arrow-up-bold-circle-outline
                      </v-icon>
                    </span>
                  </template>
                  <span>
                    {{ notaTooltip(estudiante.notas[modulo.id]) || 'Sin nota registrada' }}
                    <template v-if="esReprobado(estudiante.notas[modulo.id])">
                      <br><strong>(Clic para solicitar nivelación)</strong>
                    </template>
                  </span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>

          <!-- Si el filtro no devuelve resultados -->
          <tbody v-else>
            <tr>
              <td :colspan="kardex.modulos.length + 2" class="text-center py-6">
                <div class="empty-filter-state">
                  <v-icon size="32" color="grey lighten-1" class="mb-1">mdi-account-search-outline</v-icon>
                  <p class="font-weight-medium slate-900--text mb-1" style="font-size: 13px;">
                    No se encontraron estudiantes para los filtros aplicados
                  </p>
                  <v-btn
                    small
                    text
                    color="primary"
                    class="rounded-lg text-none"
                    @click="limpiarFiltros">
                    <v-icon x-small left>mdi-filter-remove-outline</v-icon>
                    Limpiar filtros y búsqueda
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>

          <!-- Fila de Promedios del Módulo -->
          <tfoot>
            <tr class="promedio-row">
              <td class="td-promedio-label sticky-col text-left" colspan="2">
                <div class="d-flex align-center">
                  <v-icon x-small color="primary" class="mr-1.5">mdi-calculator-variant</v-icon>
                  PROMEDIO DEL MÓDULO
                </div>
              </td>
              <td
                v-for="modulo in kardex.modulos"
                :key="modulo.id"
                class="td-promedio text-center">
                <span class="promedio-badge">
                  {{ formatNota(calcularPromedioModulo(modulo.id)) }}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Leyenda Interactiva -->
      <div class="grades-legend mt-3 mb-1 px-2 py-2">
        <span class="legend-title mr-2">
          <v-icon x-small color="grey" class="mr-1">mdi-information-outline</v-icon>
          Leyenda de Calificaciones:
        </span>
        <span class="legend-item">
          <span class="legend-dot dot-aprobado"></span>≥ {{ notaAprobacion }} Aprobado regular
        </span>
        <span class="legend-item">
          <span class="legend-dot dot-nivelacion"></span>Aprobado por nivelación
        </span>
        <span class="legend-item">
          <span class="legend-dot dot-reprobado"></span>&lt; {{ notaAprobacion }} Reprobado (Clic para solicitar)
        </span>
        <span class="legend-item">
          <span class="legend-dot dot-cero"></span>0 Sin calificación
        </span>
        <span class="legend-item">
          <span class="legend-dot dot-vacia"></span>— No registrada
        </span>
      </div>

    </div>

    <!-- ── Estado Vacío (Sin programa cargado) ───────────────────────── -->
    <div v-else class="px-6 py-8">
      <div class="state-box empty-hero-card">
        
        <div class="hero-icon-container mb-3">
          <v-icon size="36" color="primary">mdi-school-outline</v-icon>
        </div>

        <h3 class="hero-title">Consulta de Acta Centralizadora</h3>
        <p class="hero-subtitle">
          Selecciona un programa académico en el buscador superior para visualizar las calificaciones consolidadas,
          actas de módulo y estadísticas de rendimiento.
        </p>

        <div class="empty-steps mt-4">
          
          <div class="empty-step">
            <div class="step-icon-wrap">
              <v-icon size="24" color="primary">mdi-magnify</v-icon>
            </div>
            <div class="step-num">Paso 1</div>
            <div class="step-title">Buscar Programa</div>
            <div class="step-text">Ingresa el nombre o código del programa</div>
          </div>

          <v-icon size="22" color="grey lighten-1" class="step-arrow">mdi-chevron-right</v-icon>

          <div class="empty-step">
            <div class="step-icon-wrap step-icon-2">
              <v-icon size="24" color="light-blue darken-2">mdi-table-search</v-icon>
            </div>
            <div class="step-num step-num-2">Paso 2</div>
            <div class="step-title">Cargar Acta</div>
            <div class="step-text">Presiona el botón "Ver Acta"</div>
          </div>

          <v-icon size="22" color="grey lighten-1" class="step-arrow">mdi-chevron-right</v-icon>

          <div class="empty-step">
            <div class="step-icon-wrap step-icon-3">
              <v-icon size="24" color="success">mdi-file-document-multiple-outline</v-icon>
            </div>
            <div class="step-num step-num-3">Paso 3</div>
            <div class="step-title">Gestionar y Exportar</div>
            <div class="step-text">Revisa notas, nivelaciones y descarga el PDF</div>
          </div>

        </div>

      </div>
    </div>

    <!-- Modales y Notificaciones -->
    <dialog-calificacion-modulo ref="dialogModulo" />
    <dialog-calificacion-centralizado ref="dialogCentralizado" />
    <dialog-solicitud-nivelacion ref="dialogNivelacion" @actualizado="buscarKardex" />
    <notifications group="notifications" position="top right" />
  </div>
</template>

<script>
import CalificacionCentralizadoService from '../../services/calificacion-centralizado.service';
import ProgramasService from '../../services/programas.service';
import DialogCalificacionModulo from '../components/DialogCalificacionModulo.vue';
import DialogCalificacionCentralizado from '../components/DialogCalificacionCentralizado.vue';
import DialogSolicitudNivelacion from '../components/DialogSolicitudNivelacion.vue';

const ROMANOS = [
  'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
  'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX',
];

export default {
  name: 'CalificacionCentralizadorLayout',
  components: { DialogCalificacionModulo, DialogCalificacionCentralizado, DialogSolicitudNivelacion },
  data() {
    return {
      programaId: null,
      programaSeleccionado: null,
      programas: [],
      buscandoProgramas: false,
      searchPrograma: null,
      retardo: null,
      kardex: null,
      cargando: false,
      modulosPanel: -1,
      notaAprobacion: 71,
      busquedaEstudiante: '',
      filtroEstadoEstudiante: 'todos',
    };
  },
  watch: {
    searchPrograma(val) {
      if (!val || val.length < 2) return;
      clearTimeout(this.retardo);
      this.retardo = setTimeout(() => this.fetchProgramas(val), 400);
    },
  },
  computed: {
    estudiantesFiltrados() {
      if (!this.kardex) return [];
      let lista = this.kardex.estudiantes || [];

      if (this.filtroEstadoEstudiante === 'aprobados') {
        lista = lista.filter((e) => this.calcularNotaFinal(e.notas) >= this.notaAprobacion);
      } else if (this.filtroEstadoEstudiante === 'reprobados') {
        lista = lista.filter((e) => this.calcularNotaFinal(e.notas) < this.notaAprobacion && this.calcularNotaFinal(e.notas) >= 0);
      } else if (this.filtroEstadoEstudiante === 'nivelacion') {
        lista = lista.filter((e) => this.tieneNivelacion(e));
      }

      const busqueda = this.busquedaEstudiante?.trim().toLowerCase();
      if (!busqueda) return lista;
      return lista.filter((e) => e.nombreCompleto?.toLowerCase().includes(busqueda)
        || e.ci?.toLowerCase().includes(busqueda));
    },
    estudiantesAprobados() {
      if (!this.kardex) return 0;
      return this.kardex.estudiantes.filter(
        (e) => this.calcularNotaFinal(e.notas) >= this.notaAprobacion,
      ).length;
    },
    estudiantesReprobados() {
      if (!this.kardex) return 0;
      return this.kardex.estudiantes.filter(
        (e) => this.calcularNotaFinal(e.notas) < this.notaAprobacion && this.calcularNotaFinal(e.notas) >= 0,
      ).length;
    },
    estudiantesConNivelacion() {
      if (!this.kardex) return 0;
      return this.kardex.estudiantes.filter((e) => this.tieneNivelacion(e)).length;
    },
    porcentajeAprobados() {
      if (!this.kardex?.estudiantes?.length) return 0;
      return Math.round((this.estudiantesAprobados / this.kardex.estudiantes.length) * 100);
    },
    porcentajeReprobados() {
      if (!this.kardex?.estudiantes?.length) return 0;
      return Math.round((this.estudiantesReprobados / this.kardex.estudiantes.length) * 100);
    },
  },
  methods: {
    abrirDialogModulo(modulo) {
      this.$refs.dialogModulo.abrir(modulo);
    },
    esReprobado(nota) {
      const notaConsolidada = nota?.notaConsolidada;
      return notaConsolidada !== null && notaConsolidada !== undefined && notaConsolidada >= 0 && notaConsolidada < this.notaAprobacion;
    },
    tieneNivelacion(estudiante) {
      if (!estudiante?.notas) return false;
      return Object.values(estudiante.notas).some((n) => n?.aprobado === 'nivelacion' || (n?.notaNivelacion !== null && n?.notaNivelacion !== undefined));
    },
    aprobadoLabel(aprobado) {
      if (aprobado === 'notaFinal') return 'Nota regular';
      if (aprobado === 'recuperatorio') return 'Recuperatorio';
      if (aprobado === 'nivelacion') return 'Nivelación';
      return '';
    },
    notaTooltip(nota) {
      if (!nota) return '';
      const partes = [];
      if (nota.notaFinal !== null && nota.notaFinal !== undefined) {
        partes.push(`Regular: ${nota.notaFinal}`);
      }
      if (nota.notaRecuperatorio !== null && nota.notaRecuperatorio !== undefined) {
        partes.push(`Recup: ${nota.notaRecuperatorio}`);
      }
      if (nota.notaNivelacion !== null && nota.notaNivelacion !== undefined) {
        partes.push(`Nivelación: ${nota.notaNivelacion}`);
      }
      if (nota.aprobado) {
        partes.push(`Origen: ${this.aprobadoLabel(nota.aprobado)}`);
      }
      return partes.join(' · ');
    },
    getInitials(name) {
      if (!name) return 'ES';
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    limpiarFiltros() {
      this.busquedaEstudiante = '';
      this.filtroEstadoEstudiante = 'todos';
    },
    abrirDialogNivelacion(estudiante, modulo) {
      const inscripcionModuloId = estudiante.inscripcionesModulo?.[modulo.id];
      if (!inscripcionModuloId) return;
      const nota = estudiante.notas[modulo.id];
      this.$refs.dialogNivelacion.abrir({
        inscripcionModuloId,
        estudianteNombre: estudiante.nombreCompleto,
        estudianteCi: estudiante.ci,
        moduloNombre: modulo.nombre,
        moduloNumero: this.toRoman(modulo.numero),
        notaActual: nota?.notaConsolidada,
        notaRecuperatorio: nota?.notaRecuperatorio,
        notaNivelacion: nota?.notaNivelacion,
      });
    },
    abrirDialogCentralizado() {
      const nombre = this.kardex?.programa?.nombre || `Programa ${this.programaId}`;
      const programaUniversidadId = this.kardex?.programa?.programaUniversidadId || null;
      this.$refs.dialogCentralizado.abrir(this.programaId, nombre, programaUniversidadId);
    },
    onProgramaSeleccionado(programa) {
      if (!programa) {
        this.programaId = null;
        this.kardex = null;
        return;
      }
      this.programaId = typeof programa === 'object' ? programa.id : programa;
      this.buscarKardex();
    },
    async fetchProgramas(q) {
      this.buscandoProgramas = true;
      try {
        const { data } = await ProgramasService.buscarProgramas({ q });
        this.programas = data?.datos || [];
      } catch {
        this.programas = [];
      } finally {
        this.buscandoProgramas = false;
      }
    },
    toRoman(num) {
      return ROMANOS[num - 1] || String(num);
    },
    formatNota(nota) {
      const notaConsolidada = nota?.notaConsolidada;
      if (notaConsolidada === null || notaConsolidada === undefined) return '—';
      return notaConsolidada;
    },
    calcularNotaFinal(notas) {
      if (!notas) return 0;
      const valores = Object.values(notas)
        .map((n) => n?.notaConsolidada)
        .filter((v) => v !== null && v !== undefined);
      if (valores.length === 0) return 0;
      const suma = valores.reduce((acc, v) => acc + v, 0);
      return Math.round(suma / valores.length);
    },
    calcularPromedioModulo(moduloId) {
      if (!this.kardex) return null;
      const valores = this.kardex.estudiantes
        .map((e) => e.notas[moduloId]?.notaConsolidada)
        .filter((v) => v !== null && v !== undefined && v > 0);
      if (valores.length === 0) return null;
      return Math.round(valores.reduce((acc, v) => acc + v, 0) / valores.length);
    },
    calcularPromedioGeneral() {
      if (!this.kardex?.estudiantes?.length) return 0;
      const notasFinales = this.kardex.estudiantes
        .map((e) => this.calcularNotaFinal(e.notas))
        .filter((n) => n > 0);
      if (notasFinales.length === 0) return 0;
      const suma = notasFinales.reduce((acc, v) => acc + v, 0);
      return Math.round(suma / notasFinales.length);
    },
    notaClass(nota) {
      const notaConsolidada = nota?.notaConsolidada;
      if (notaConsolidada === null || notaConsolidada === undefined) return 'nota--vacia';
      if (notaConsolidada === 0) return 'nota--cero';
      if (notaConsolidada >= this.notaAprobacion) {
        return nota.aprobado === 'nivelacion' ? 'nota--nivelacion' : 'nota--aprobado';
      }
      return 'nota--reprobado';
    },
    async buscarKardex() {
      if (!this.programaId) return;

      this.cargando = true;
      this.kardex = null;
      this.busquedaEstudiante = '';
      this.filtroEstadoEstudiante = 'todos';

      try {
        const { data } = await CalificacionCentralizadoService.getProgramaKardex({
          formato: 'vista',
          programaId: this.programaId,
        });
        this.kardex = data?.datos || null;
        this.notaAprobacion = this.kardex?.programa?.notaAprobacion || 71;
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al obtener el kardex';
        this.$notify({
          group: 'notifications',
          title: 'Error',
          text: mensaje,
          type: 'error',
        });
      } finally {
        this.cargando = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Contenedor Raíz ────────────────────────────────────────────── */
.centralizador-root {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  margin: 0 4px;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.03), 0 2px 4px -1px rgba(0, 0, 0, 0.02);
}

/* Header style */
.dialog-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e2e8f0;
}

.filter-header-icon {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 12px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #bfdbfe;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.08);
}

.slate-900--text {
  color: #0f172a !important;
}

/* Search container */
.search-program-container {
  min-width: 260px;
  max-width: 540px;
}

/* Custom inputs style */
.custom-input ::v-deep .v-input__slot {
  background-color: #f8fafc !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 10px !important;
  transition: all 0.2s ease;
  box-shadow: none !important;
  min-height: 40px !important;
}

.custom-input ::v-deep .v-input__slot:hover {
  background-color: #f1f5f9 !important;
  border-color: #94a3b8 !important;
}

.custom-input ::v-deep .v-input__slot:focus-within {
  background-color: #ffffff !important;
  border-color: #2563eb !important;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12) !important;
}

.custom-input ::v-deep fieldset {
  border: none !important;
}

.custom-input ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.custom-input ::v-deep .v-input__append-inner {
  margin-top: 8px !important;
}

.custom-input ::v-deep .v-icon {
  color: #64748b !important;
  font-size: 18px !important;
}

.custom-input ::v-deep .v-select__selection {
  color: #1e293b !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
}

.custom-input ::v-deep input {
  color: #1e293b !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
}

.custom-input-sm ::v-deep .v-input__slot {
  min-height: 34px !important;
  border-radius: 8px !important;
}

.custom-input-sm ::v-deep .v-input__prepend-inner {
  margin-top: 5px !important;
}

.custom-input-sm ::v-deep .v-input__append-inner {
  margin-top: 5px !important;
}

/* Action button styles */
.action-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  height: 40px !important;
  border-radius: 10px !important;
  transition: all 0.2s ease !important;
}

.apply-btn {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
  color: #ffffff !important;
  box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.25), 0 2px 4px -1px rgba(37, 99, 235, 0.15) !important;
}

.apply-btn:hover {
  box-shadow: 0 6px 10px -1px rgba(37, 99, 235, 0.35), 0 4px 6px -1px rgba(37, 99, 235, 0.2) !important;
  transform: translateY(-1px);
}

.apply-btn.v-btn--disabled {
  background: #e2e8f0 !important;
  color: #94a3b8 !important;
  box-shadow: none !important;
  border-color: #e2e8f0 !important;
}

.pdf-btn {
  border-color: #fecdd3 !important;
  background-color: #fff1f2 !important;
  color: #be123c !important;
}

.pdf-btn:hover {
  background-color: #ffe4e6 !important;
  border-color: #fda4af !important;
  transform: translateY(-1px);
}

.compact-btn-icon {
  width: 40px !important;
  min-width: 40px !important;
  padding: 0 !important;
}

/* ── Program Card & KPI Overview ────────────────────────────────── */
.program-card {
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  background: #ffffff !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.04) !important;
  overflow: hidden;
}

.program-card-body {
  padding: 16px 20px;
}

.prog-nombre {
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
  letter-spacing: -0.01em;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.04);
}

.kpi-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kpi-blue .kpi-icon-wrap {
  background: #eff6ff;
  border: 1px solid #dbeafe;
}

.kpi-slate .kpi-icon-wrap {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.kpi-emerald .kpi-icon-wrap {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}

.kpi-rose .kpi-icon-wrap {
  background: #fff1f2;
  border: 1px solid #fecdd3;
}

.kpi-content {
  display: flex;
  flex-direction: column;
}

.kpi-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.1;
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.kpi-pct {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}

.kpi-label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
  margin-top: 2px;
}

.kpi-progress {
  min-width: 220px;
}

/* ── Badges y Chips ─────────────────────────────────────────────── */
.custom-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 9px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 9999px;
  line-height: 1.25;
  white-space: nowrap;
}

.badge-xs {
  font-size: 0.7rem;
  padding: 2px 7px;
}

.program-code-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 6px;
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.state-version {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.badge-uni {
  background-color: #f3e8ff;
  color: #6b21a8;
  border: 1px solid #d8b4fe;
}

.badge-sede {
  background-color: #e0f2fe;
  color: #0369a1;
  border: 1px solid #bae6fd;
}

.badge-threshold {
  background-color: #f8fafc;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.state-count {
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #dbeafe;
}

.state-default {
  background-color: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.state-concluido {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.state-no-iniciado {
  background-color: #fff1f2;
  color: #9f1239;
  border: 1px solid #fecdd3;
}

.req-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: 8px;
  white-space: nowrap;
}

.req-success {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

/* Mini autocomplete chips */
.chip-mini {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  font-size: 0.68rem;
  border-radius: 4px;
  font-weight: 600;
}

.chip-code {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #dbeafe;
}

.chip-uni {
  background: #f3e8ff;
  color: #6b21a8;
  border: 1px solid #e9d5ff;
}

.chip-sede {
  background: #e0f2fe;
  color: #0369a1;
  border: 1px solid #bae6fd;
}

/* ── Acordeón de Módulos ────────────────────────────────────────── */
.modulos-panel {
  border: 1px solid #e2e8f0;
  border-radius: 14px !important;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.03);
}

::v-deep .modulos-panel .v-expansion-panel {
  border-radius: 14px !important;
}

::v-deep .modulos-panel .v-expansion-panel::before {
  box-shadow: none !important;
}

.modulos-panel-header {
  background-color: #f8fafc !important;
  min-height: 48px !important;
  padding: 0 18px !important;
}

.header-icon-mini {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .modulos-panel .v-expansion-panel-content__wrap {
  padding: 0 !important;
}

.modulos-panel-content .table-container {
  border-top: 1px solid #e2e8f0;
}

/* Tabla de Módulos */
.modulos-table {
  border-collapse: collapse;
  width: 100%;
}

.modulos-table thead th {
  background-color: #f1f5f9 !important;
  color: #475569 !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  height: 38px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 8px 14px;
}

.modulos-table tbody tr {
  transition: background-color 0.15s ease;
}

.modulos-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.modulos-table tbody td {
  height: 44px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  font-size: 0.825rem;
  color: #334155;
  vertical-align: middle;
  padding: 8px 14px;
}

.modulo-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 24px;
  background-color: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 700;
}

.modulo-title-text {
  font-weight: 600;
  color: #1e293b;
}

.docente-info {
  display: flex;
  flex-direction: column;
}

.docente-name {
  font-weight: 600;
  color: #334155;
  font-size: 0.825rem;
}

.docente-ci {
  font-size: 0.72rem;
  color: #64748b;
  display: flex;
  align-items: center;
  margin-top: 1px;
}

.docente-unassigned {
  font-size: 0.75rem;
  color: #b45309;
  font-style: italic;
  display: inline-flex;
  align-items: center;
  background-color: #fffbeb;
  padding: 2px 8px;
  border-radius: 6px;
  border: 1px solid #fef3c7;
}

.module-acta-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  height: 28px !important;
}

/* ── Toolbar de la Matriz ────────────────────────────────────────── */
.matrix-toolbar {
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 10px;
}

.section-label {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
}

/* Filter tabs */
.filter-tabs-wrap {
  display: inline-flex;
  background: #f1f5f9;
  padding: 3px;
  border-radius: 10px;
  gap: 2px;
}

.filter-tab-btn {
  border: none;
  background: transparent;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.filter-tab-btn:hover {
  color: #1e293b;
}

.filter-tab-active {
  background: #ffffff;
  color: #2563eb !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.tab-success.filter-tab-active {
  color: #059669 !important;
}

.tab-error.filter-tab-active {
  color: #e11d48 !important;
}

.tab-warning.filter-tab-active {
  color: #d97706 !important;
}

/* ── Matriz de Calificaciones (Grades Table) ─────────────────────── */
.detalle-pago-docente-container {
  border: 1px solid #cbd5e1;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.04);
  overflow-x: auto;
}

.grades-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.grades-table thead th {
  background-color: #f8fafc !important;
  color: #1e293b !important;
  font-weight: 700 !important;
  font-size: 0.72rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  border-bottom: 2px solid #cbd5e1 !important;
  height: 46px !important;
  padding: 8px 10px;
  white-space: nowrap;
}

.th-modulo-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.th-romano {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 22px;
  border-radius: 4px;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 700;
  font-size: 0.72rem;
  border: 1px solid #bfdbfe;
}

.grades-table tbody tr {
  transition: background-color 0.15s ease;
}

.grades-table tbody tr:nth-child(even) {
  background-color: #fbfcfe;
}

.grades-table tbody tr:hover {
  background-color: #eff6ff !important;
}

.grades-table tbody td {
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 8px 10px;
  color: #334155;
  font-size: 0.825rem;
  vertical-align: middle;
}

/* Sticky columns */
.th-idx, .td-idx {
  width: 38px !important;
  min-width: 38px !important;
  max-width: 38px !important;
}

.th-nombre, .td-nombre {
  min-width: 240px;
  max-width: 320px;
}

.th-nota, .td-nota {
  width: 56px;
  min-width: 56px;
}

.th-final, .td-final {
  width: 80px;
  min-width: 80px;
  border-left: 1px dashed #cbd5e1 !important;
}

.th-estado, .td-estado {
  width: 90px;
  min-width: 90px;
}

.grades-table .sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: inherit;
}

.grades-table .sticky-col-2 {
  position: sticky;
  left: 38px;
  z-index: 2;
  background-color: inherit;
  border-right: 2px solid #cbd5e1 !important;
  box-shadow: 3px 0 6px -2px rgba(0, 0, 0, 0.06);
}

.grades-table thead th.sticky-col {
  z-index: 3;
  background-color: #f8fafc !important;
}

.grades-table thead th.sticky-col-2 {
  z-index: 3;
  background-color: #f8fafc !important;
  border-right: 2px solid #cbd5e1 !important;
}

.td-idx {
  text-align: center;
  color: #64748b;
  font-weight: 600;
  font-size: 0.75rem;
}

/* Student info cell */
.student-cell-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.student-avatar-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1d4ed8;
  font-weight: 700;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #bfdbfe;
  flex-shrink: 0;
}

.student-details {
  display: flex;
  flex-direction: column;
}

.student-name {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.85rem;
  line-height: 1.25;
}

.student-ci {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  color: #64748b;
  margin-top: 1px;
  font-weight: 500;
}

/* Grade badges */
.nota-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 26px;
  font-size: 0.8rem;
  font-weight: 700;
  text-align: center;
  padding: 2px 6px;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.nota--vacia {
  color: #94a3b8;
  font-weight: 500;
}

.nota--cero {
  background-color: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.nota--aprobado {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.nota--nivelacion {
  background-color: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.nota--reprobado {
  background-color: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
}

.td-nota--clickable {
  cursor: pointer;
}

.td-nota--clickable .nota--reprobado:hover {
  background-color: #ffe4e6;
  border-color: #fda4af;
  transform: scale(1.06);
}

.action-indicator-icon {
  margin-left: 2px;
}

/* Final Grade badges */
.nota-final-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 26px;
  border-radius: 6px;
  font-size: 0.825rem;
  font-weight: 800;
}

.final--cero {
  background-color: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
}

.final--aprobado {
  background-color: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.final--reprobado {
  background-color: #fff1f2;
  color: #be123c;
  border: 1px solid #fecdd3;
}

/* Footer / Promedios row */
.promedio-row {
  background-color: #f1f5f9 !important;
  font-weight: 700;
  border-top: 2px solid #cbd5e1;
}

.promedio-row td {
  border-bottom: none !important;
  padding: 10px !important;
}

.td-promedio-label {
  font-size: 0.75rem;
  font-weight: 800;
  color: #334155;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-right: 2px solid #cbd5e1 !important;
  box-shadow: 3px 0 6px -2px rgba(0, 0, 0, 0.06);
}

.promedio-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 24px;
  font-size: 0.75rem;
  font-weight: 800;
  border-radius: 5px;
  color: #1e3a8a;
  background: #dbeafe;
  border: 1px solid #bfdbfe;
}

.promedio-final-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  height: 26px;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 800;
  color: #ffffff;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.25);
}

/* ── Leyenda de Colores ─────────────────────────────────────────── */
.grades-legend {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.legend-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
  display: inline-flex;
  align-items: center;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  color: #475569;
  font-weight: 600;
}

.legend-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 4px;
  flex-shrink: 0;
}

.dot-aprobado  { background: #ecfdf5; border: 1px solid #a7f3d0; }
.dot-nivelacion { background: #fffbeb; border: 1px solid #fde68a; }
.dot-reprobado { background: #fff1f2; border: 1px solid #fecdd3; }
.dot-cero      { background: #f1f5f9; border: 1px solid #cbd5e1; }
.dot-vacia     { background: transparent; border: 1px dashed #94a3b8; }

/* ── Estados (Loading / Vacío) ──────────────────────────────────── */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  padding: 40px 20px;
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
}

.loading-spinner-wrap {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #bfdbfe;
}

.state-text {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  text-align: center;
}

.state-subtext {
  font-size: 0.85rem;
  font-weight: 500;
  color: #64748b;
  margin: 4px 0 0;
  text-align: center;
  max-width: 500px;
}

/* Empty Hero Card */
.empty-hero-card {
  background: radial-gradient(circle at center, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
}

.hero-icon-container {
  width: 70px;
  height: 70px;
  border-radius: 20px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #bfdbfe;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(37, 99, 235, 0.12);
}

.hero-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 6px;
}

.hero-subtitle {
  font-size: 0.85rem;
  color: #64748b;
  text-align: center;
  max-width: 560px;
  line-height: 1.4;
  margin: 0;
}

/* Empty steps */
.empty-steps {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
}

.empty-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  width: 170px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.step-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.step-icon-2 {
  background: #e0f2fe;
  border-color: #bae6fd;
}

.step-icon-3 {
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.step-num {
  font-size: 0.7rem;
  font-weight: 700;
  color: #1d4ed8;
  background: #dbeafe;
  border-radius: 99px;
  padding: 2px 8px;
  margin-bottom: 6px;
}

.step-num-2 {
  color: #0369a1;
  background: #e0f2fe;
}

.step-num-3 {
  color: #065f46;
  background: #d1fae5;
}

.step-title {
  font-size: 0.825rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 2px;
}

.step-text {
  font-size: 0.72rem;
  color: #64748b;
  line-height: 1.25;
}

.step-arrow {
  margin-bottom: 12px;
}

/* Empty filter in table */
.empty-filter-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* ── Content Fade Animation ─────────────────────────────────────── */
.content-fade {
  animation: fadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

/* ── Autocomplete styling ───────────────────────────────────────── */
.autocomplete-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1e293b;
  white-space: normal;
  line-height: 1.35;
}

.autocomplete-codigo {
  font-size: 0.75rem;
  color: #64748b;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.autocomplete-selected {
  font-size: 0.85rem;
  color: #1e293b;
  max-width: 100%;
}
</style>
